import { Checkbox, Flex, GmgTheme, toast } from '@gmg/gmg-react-components';
import { FunctionComponent, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import AppContext from 'src/AppContext';
import { useAssignLicenseSystemToOrg } from 'src/graphql/useAssignLicenseSystemToOrg';
import { isGmgAdminRole } from 'src/role';
import { organizationPaths } from 'src/routes';
import { getConnectToManageLicensesEvent } from 'src/tracking';
import { LicenseSystem } from 'src/viewModels/LicenseSystem';
import { useTheme } from 'styled-components';
import LicenseGraphicDark from '../../assets/svg/graphic_license_dark.svg';
import LicenseGraphicLight from '../../assets/svg/graphic_license_light.svg';
import { default as IconKey, default as IconKeyFilled } from '../../assets/svg/icon_key_filled.svg';
import OrganizationContainer from '../OrganizationContainer';
import { LocationWithCustomState } from '../appService/RedirectToLicenseManagement';
import RequestTemporaryLicensesModalContainer from '../appService/RequestTemporaryLicensesModalContainer';
import EmptyPage from '../shared/EmptyPage';
import SearchBox from '../shared/SearchBox';
import LicenseManagementTable from './LicenseManagementTable';
import AddExistingLicenseSystemToOrgButton from './addExsitingLicenseSystemToOrg/AddExistingLicenseSystemToOrgButton';
import AddNewLicenseSystemToOrgButton from './addNewLicenseSystemToOrg/AddNewLicenseSystemToOrgButton';

export interface LicenseManagementProps { };

const LicenseManagement: FunctionComponent<LicenseManagementProps> = () => {
    const { t } = useTranslation();
    const theme: GmgTheme = useTheme();
    const { user, trackEvent } = useContext(AppContext);
    const { onShowModal } = useContext(AppContext);
    const location = useLocation() as LocationWithCustomState;
    const navigate = useNavigate();
    const isAllowedToUseLicenseSystemsOfflineRef = useRef(false);
    const isTemporaryLicensesRequest = location.state?.isTemporaryLicensesRequest;
    const requestedLicenseSystemIdFromAppService = location.state?.licenseSystemId;
    const isUserComingFromAppService = location.state?.redirectedFromAppService;
    const isUserGmgAdminOrSupport = isGmgAdminRole(user?.role);

    const [licenseSystemList, setLicenseSystemList] = useState<Array<LicenseSystem> | undefined>(undefined);
    const [isExpiredLicensesFilterActive, setIsExpiredLicensesFilterActive] = useState<boolean>(true);
    const [filterValue, setFilterValue] = useState<string>('');

    const isSystemLicensesLoading = licenseSystemList === undefined;
    const assignLicenseSystemToOrg = useAssignLicenseSystemToOrg(user!.orgId!);

    const iconEmptyPage = theme.type === 'dark'
        ? <LicenseGraphicDark width={255} height={255} />
        : <LicenseGraphicLight width={255} height={255} />;

    useEffect(() => {
        const isNotPossibleToAssignLicenseSystemToOrg =
            isSystemLicensesLoading ||
            !isUserComingFromAppService ||
            !requestedLicenseSystemIdFromAppService;

        if (isNotPossibleToAssignLicenseSystemToOrg) return;

        const isLicenseSystemAlreadyAssignedToOrg = licenseSystemList.find(license => license.id === requestedLicenseSystemIdFromAppService);
        if (isLicenseSystemAlreadyAssignedToOrg) {
            // This is a shortCut useCase to jump into Cmp from appService
            return;
        }

        trackEvent(getConnectToManageLicensesEvent());

        void toast.promise(assignLicenseSystemToOrg.mutateAsync(requestedLicenseSystemIdFromAppService), {
            loading: t('Common.Toast.Loading', 'Loading ...'),
            success: t('AssignLicenseSystemToOrganization.toast_success_desc', {
                defaultValue: 'You have successfully added license system "{{serialNumber}}" to your organization.',
                serialNumber: requestedLicenseSystemIdFromAppService,
            }),
            error: (response) => response.errors[0].message,
        });

        // Navigate resets the location state. If we don't do it, assign license system to org will be triggered again after browser refresh.
        navigate(organizationPaths.systemLicences(user!.orgId!));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedLicenseSystemIdFromAppService, licenseSystemList, isSystemLicensesLoading, isUserComingFromAppService]);

    useEffect(() => {
        const isNotPossibleToRequestTemporaryLicenses =
            !isUserComingFromAppService ||
            !isTemporaryLicensesRequest ||
            !requestedLicenseSystemIdFromAppService;

        if (isNotPossibleToRequestTemporaryLicenses) return;

        onShowModal('inputModal', {
            title: t('RequestTemporaryLicenses.Modal_title', 'Request temporary licenses'),
            titleIcon: <IconKey />,
            content: <RequestTemporaryLicensesModalContainer temporaryLicensesSerialNumber={requestedLicenseSystemIdFromAppService} />,
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedLicenseSystemIdFromAppService, isTemporaryLicensesRequest, isUserComingFromAppService]);

    return (
        <OrganizationContainer
            onOrganizationLoad={({ licenseSystems, isAllowedToUseLicenseSystemsOffline }) => {
                isAllowedToUseLicenseSystemsOfflineRef.current = isAllowedToUseLicenseSystemsOffline;
                setLicenseSystemList(licenseSystems);
            }}
            viewInfo={{
                icon: <IconKeyFilled width={16} height={16} />,
                label: t('HeaderPageTitles.license_management_title', 'License Management'),
            }}
            checkBox={
                <Checkbox
                    aria-label="hide expired licenses"
                    id="filterExpiredLicenses"
                    label={t('LicenseManagementTable.checkbox_filter_expired_licenses', 'Hide expired licenses')}
                    defaultChecked={isExpiredLicensesFilterActive}
                    onChange={() => setIsExpiredLicensesFilterActive(!isExpiredLicensesFilterActive)}
                />
            }
            searchBar={
                <SearchBox
                    onChange={(e) => setFilterValue(e.target.value)}
                    value={filterValue}
                />
            }
            button={<>
                {isUserGmgAdminOrSupport ? <AddExistingLicenseSystemToOrgButton /> : null}
                {isAllowedToUseLicenseSystemsOfflineRef.current ? <AddNewLicenseSystemToOrgButton /> : null}

            </>}
        >
            <Flex flexDirection='column' maxWidth='calc(100vw - 86px)'>
                {isSystemLicensesLoading
                    ? <></>
                    // Quick fix not to see the spinner 'cos at that point we have already fetched the data
                    // and there is actually no loading state but react needs a tiny amount of time to update the ui
                    // React.suspense should be introduced with fallback UI
                    : licenseSystemList.length === 0
                        ? <EmptyPage
                            image={iconEmptyPage}
                            title={t('EmptyLicensePage.pageTitle_title', 'Add license systems')}
                            description={t('EmptyLicensePage.information_desc', 'Add license systems by pairing each locally with the Application Service (open the Application Service menu and click \'GMG Manage\'). This way you can create an overview of all systems here, showing the licenses of each system together with all essential information."')}
                        />
                        : <LicenseManagementTable
                            licenseSystemList={licenseSystemList}
                            isFilteringExpiredLicenses={isExpiredLicensesFilterActive}
                            filterValue={filterValue}
                            isAllowedToUseLicenseSystemsOffline={isAllowedToUseLicenseSystemsOfflineRef.current}
                        />
                }
            </Flex>
        </OrganizationContainer>
    );
};

export const isLicenseSystemIdFormatValid = (id: string) => {
    if (!id) return false;
    const systemIdRegex = /^\d{1}\.\d{3}\.\d{3}\.\d{3}$/;
    return !!id.match(systemIdRegex);
};

export default LicenseManagement;
